import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Target, Users, Award, BookOpen, TrendingUp, CheckCircle2, Zap, Phone } from 'lucide-react';
import { SectionTitle } from '@/components/common/SectionTitle';
import { CourseCard } from '@/components/common/CourseCard';
import { StatsCard } from '@/components/common/StatsCard';
import { coursesData } from '@/data/coursesData';
import { ResultCard } from '@/components/common/ResultCard';
import { resultsShowcaseData } from '@/data/resultsShowcaseData';
import { resultsData } from '@/data/resultsData';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import HeroBanner from '../assets/images/hero/01.jpg';
import Image from '../assets/images/hero/02.jpg';
import SuccessBanner from '../assets/images/success/02.jpg';


const HomePage = () => {
  const featuredCourses = coursesData.slice(0, 3);

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${HeroBanner})`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/70 to-secondary/80" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent-gold rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-light rounded-full blur-3xl animate-float"
            style={{ animationDelay: "1s" }}
          />
          <div
            className="absolute top-1/2 left-1/2 w-80 h-80 bg-primary-light rounded-full blur-3xl animate-float"
            style={{ animationDelay: "2s" }}
          />
        </div>

        <div className="container-custom relative z-10 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-primary-foreground"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                <Badge className="mb-6 bg-accent-gold/20 text-accent-gold border-accent-gold/30 px-4 py-2 text-sm">
                  🏆 Established in 2021 | Excellence in Competitive Education
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mb-6 leading-tight"
              >
                Shape Your Future at
                <span className="text-accent-gold"> Mantra Prayas</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="text-lg sm:text-xl text-primary-foreground/90 mb-8 leading-relaxed"
              >
                Premier coaching institute in Bhilwara for IIT-JEE, NEET preparation. Transform your dreams into reality with expert guidance.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/enquiry">
                  <Button size="lg" className="bg-secondary hover:bg-secondary-dark text-secondary-foreground shadow-lg hover:shadow-xl transition-all group text-base px-8">
                    Enquire Now
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/courses">
                  <Button size="lg" variant="outline" className="bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/20 text-base px-8">
                    Explore Courses
                  </Button>
                </Link>
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="grid grid-cols-3 gap-6 mt-12 pt-12 border-t border-primary-foreground/20"
              >
                {[
                  { label: 'Students', value: '1000+' },
                  { label: 'Years of Excellence', value: '5+' },
                  { label: 'Expert Faculty', value: '13+' }
                ].map((stat, idx) => (
                  <div key={idx}>
                    <div className="text-3xl sm:text-4xl font-heading font-bold text-accent-gold">
                      {stat.value}
                    </div>
                    <div className="text-sm text-primary-foreground/80 mt-1">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hidden lg:flex justify-end"
            >
              <div className="relative bg-white/10 backdrop-blur-xl p-5 rounded-[3rem] shadow-2xl">

                <img
                  src={Image}
                  alt="Students Studying"
                  className="rounded-[2.5rem] w-[420px] h-[520px] object-cover"
                />

                {/* Floating Result Card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-5 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 bg-primary rounded-full flex items-center justify-center">
                      <Award className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Top Result</p>
                      <p className="text-2xl font-bold text-foreground">99.92%ile</p>
                    </div>
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Institute Badges */}
      <section className="py-8 bg-surface-alt border-y border-border">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center lg:gap-8 gap-3">
            {[
              { icon: '🎯', text: 'IIT-JEE' },
              { icon: '⚕️', text: 'NEET' },
              { icon: '📚', text: 'OLYMPIAD' }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="flex items-center space-x-2 px-4 py-2 bg-card rounded-lg border border-border"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="font-semibold text-foreground">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionTitle
            subtitle="Why Choose Us"
            title="Excellence in Every Aspect"
            description="At Mantra Prayas, we continuously upgrade our teaching methodology to match the latest trends, combining knowledge with smart exam-oriented strategies."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {[
              {
                icon: Users,
                title: 'Experienced Faculty',
                description: 'Highly experienced subject experts',
                color: 'text-primary',
                link: '/faculty'
              },
              {
                icon: BookOpen,
                title: 'Comprehensive Study Material',
                description: 'Well-researched and regularly updated content covering entire syllabus',
                color: 'text-secondary'
              },
              {
                icon: Target,
                title: 'Personalized Attention',
                description: 'Small batch sizes ensuring individual focus on every student',
                color: 'text-info'
              },
              {
                icon: TrendingUp,
                title: 'Regular Assessments',
                description: 'Weekly tests and All India test series to track progress',
                color: 'text-success'
              },
              {
                icon: Award,
                title: 'Proven Results',
                description: 'IITs, NITs & BITS selections',
                color: 'text-accent',
                link: '/results'
              },
              {
                icon: CheckCircle2,
                title: 'Focused Preparation',
                description: 'Speed & accuracy driven approach',
                color: 'text-warning'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={feature.link} className="block h-full">
                  <Card className="h-full border-2 hover:border-primary transition-all duration-300 hover:shadow-lg group">
                    <CardContent className="p-8">
                      <div className={`h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform ${feature.color}`}>
                        <feature.icon className="h-7 w-7" />
                      </div>
                      <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="section-padding bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="container-custom">
          <SectionTitle
            subtitle="Our Achievements"
            title="Outstanding Academic Achievements"
            description="Our students achieve consistent success in IIT-JEE, NEET and Olympiad examinations."
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {resultsData.highlights.map((stat, index) => (
              <StatsCard key={stat.id} stat={stat} index={index} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/results">
              <Button size="lg" className="bg-primary hover:bg-primary-dark text-primary-foreground group">
                View All Results
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <SectionTitle
            subtitle="Our Programs"
            title="Courses Designed for Success"
            description="Structured classroom and foundation programs from Class VI to XII Pass for competitive and board examinations."
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {featuredCourses.map((course, index) => (
              <CourseCard key={course.id} course={course} index={index} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/courses">
              <Button size="lg" variant="outline" className="border-2 group">
                View All Courses
                <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding bg-surface-alt">
        <div className="container-custom">

          <SectionTitle
            subtitle="Our Results"
            title="From Bhilwara to IITs & NITs"
            description="Real students. Real results. Proof of excellence at Mantra Prayas."
          />

          <div className="mt-16">
            <Swiper
              modules={[Autoplay, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              autoplay={{ delay: 4500, disableOnInteraction: false }}
              pagination={{ clickable: true }}
              breakpoints={{
                640: { slidesPerView: 1 },
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 4 }
              }}
              className="pb-12"
            >
              {resultsShowcaseData.map((student, index) => (
                <SwiperSlide key={student.id}>
                  <ResultCard student={student} index={index} />
                </SwiperSlide>
              ))}

            </Swiper>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-12"
            >
              <Link to="/results">
                <Button size="lg" variant="outline" className="border-2 group">
                  View All Results
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </motion.div>
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="relative min-h-[60vh] flex items-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${SuccessBanner})`,
        }} >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/50 to-secondary/50" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-accent-gold rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-light rounded-full blur-3xl animate-float"
            style={{ animationDelay: "1s" }}
          />
          <div
            className="absolute top-1/2 left-1/2 w-80 h-80 bg-primary-light rounded-full blur-3xl animate-float"
            style={{ animationDelay: "2s" }}
          />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto text-center text-primary-foreground"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-6">
              Ready to Start Your Success Journey?
            </h2>
            <p className="text-lg sm:text-xl text-primary-foreground/90 mb-10 leading-relaxed">
              Join Mantra Prayas and experience a result-oriented approach for IIT-JEE, NEET and Olympiad success.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/enquiry">
                <Button size="lg" className="bg-secondary hover:bg-secondary-dark text-secondary-foreground shadow-xl hover:shadow-2xl transition-all text-base px-8">
                  Get Started Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+918982845157">
                <Button size="lg" variant="outline" className="bg-primary-foreground/10 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/20 text-base px-8">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Us Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
